import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  delete_icon,
  edit_icon,
  Search_icon,
  view_icon,
} from "../../assets/img";
import { Table } from "../../components/Table/Table";
import Topbar from "../../components/Topbar/Topbar";
import { useGlobalContext } from "../../Services/PaginationGlobal";
import { get, post } from "../../Services/ApiHandler";
import { URLS } from "../../Services/Constant";
import { checkPermission } from "../redux/api/PermissionManager";

const AdminList = () => {
  const adminDetails = JSON.parse(localStorage.getItem("admin_details"));

  const { paginationData, setpaginationData } = useGlobalContext();
  const location = useLocation();
  const path = location.pathname?.replace("/", "");
  const navigate = useNavigate();

  const [page, setPage] = useState(paginationData?.[path]?.page ?? 1);
  const [pagination, setPagination] = useState({});
  const [selectedFilter, setSelectedFilter] = useState({
    search: paginationData?.[path]?.search ?? "",
  });
  const [adminList, setAdminList] = useState([]);
  const [count, setCount] = useState(0);
  const [popup, setPopup] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    getList(page);
    setpaginationData((prev) => ({
      ...prev,
      [path]: {
        ...prev?.[path],
        page,
        search: selectedFilter.search,
      },
    }));
  }, [selectedFilter.search, page]);

  const getList = async (pageNumber = 1) => {
    const params = new URLSearchParams({
      page: pageNumber,
      search: selectedFilter?.search,
    });
    const res = await get(`${URLS.ADMINURL}?${params.toString()}`);
    if (res?.status === "success") {
      setCount(res?.count);
      setPagination({
        current_page: pageNumber,
        last_page: res?.next ? pageNumber + 1 : pageNumber,
      });
      setAdminList(res?.data ?? []);
    }
  };

  const handleChangeFilter = (name, val) => {
    setPage(1);
    setSelectedFilter((prev) => ({ ...prev, [name]: val }));
  };

  const handleStatus = async ({ id, status }) => {
    if (!checkPermission("CAN-STATUS-ADMIN_LIST")) return;
    try {
      const res = await post(`${URLS.ADMINURL}${id}/status/`, {
        status: !status,
      });
      if (res?.status === "success") {
        toast.success(res.message || "Status updated");
        getList(page);
      }
    } catch (err) {
      console.error("Status update error:", err);
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async () => {
    if (adminDetails?.id === deleteId) {
      toast.error("You can't delete yourself");
      return;
    }
    try {
      const res = await post(`${URLS.ADMINURL}${deleteId}/delete/`);
      if (res?.status === "success") {
        toast.success(res.message || "Deleted successfully");
        getList(page);
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Delete failed");
    } finally {
      setPopup(false);
    }
  };

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        Header: "S.No",
        accessor: "",
        Cell: ({ row }) => <p>{(page - 1) * 10 + (row.index + 1)}</p>,
      },
      {
        Header: "Admin ID",
        accessor: "admin_code",
        Cell: ({ value }) => <p>{value}</p>,
      },
      {
        Header: "Admin",
        accessor: "name",
        Cell: ({ value }) => <p>{value}</p>,
      },
      {
        Header: "Mobile Number",
        accessor: "mobile",
        Cell: ({ value }) => <p>{value}</p>,
      },
      {
        Header: "Email",
        accessor: "email",
        Cell: ({ value }) => <p>{value}</p>,
      },
      {
        Header: "Role",
        accessor: "role_name",
        Cell: ({ value }) => <p className="text-nowrap">{value}</p>,
      },
      {
        Header: "WhatsApp OTP",
        accessor: "is_otp_enabled",
        Cell: ({ value }) => (
          <p
            className="text-nowrap"
            style={{ color: value ? "#1c7102" : "#f79000" }}
          >
            {value ? "Active" : "In-Active"}
          </p>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }) => {
          if (!row?.original || row.original.id === adminDetails?.id)
            return null;
          return (
            <button
              onClick={() => handleStatus(row.original)}
              className={`${
                row.original.status ? "active-btn" : "deactive-btn"
              } border-0 fs-sm-10 fs-md-12 fs-lg-14 fs-xl-13`}
            >
              {row.original.status ? "Active" : "De-active"}
            </button>
          );
        },
      },
    ];

    const actionColumn = {
      Header: "Action",
      accessor: "",
      Cell: ({ row }) => (
        <div className="d-flex ac-js gap-3">
          {checkPermission("CAN-VIEW-ADMIN_LIST") && (
            <button
              onClick={() =>
                navigate("/add-admin", {
                  state: { type: "view", id: row.original.id, page },
                })
              }
              className="cust-btn"
            >
              <img src={view_icon} alt="view" className="edit-btn" />
            </button>
          )}

          {checkPermission("CAN-EDIT-ADMIN_LIST") && (
            <button
              onClick={() =>
                navigate("/add-admin", {
                  state: { type: "edit", id: row.original.id, page },
                })
              }
              className="cust-btn"
            >
              <img src={edit_icon} alt="edit" className="edit-btn" />
            </button>
          )}

          {row.original.role_name !== "Super Admin" &&
            row.original.id !== adminDetails?.id &&
            checkPermission("CAN-DELETE-ADMIN_LIST") && (
              <button
                onClick={() => {
                  setDeleteId(row.original.id);
                  setPopup(true);
                }}
                className="cust-btn"
              >
                <img src={delete_icon} alt="delete" className="edit-btn" />
              </button>
            )}
        </div>
      ),
    };

    return checkPermission("CAN-EDIT-ADMIN_LIST") ||
      checkPermission("CAN-VIEW-ADMIN_LIST")
      ? [...baseColumns, actionColumn]
      : baseColumns;
  }, [page]);

  return (
    <div>
      <Topbar header={"Admin List"} />
      <div className="d-flex ac-jb">
        <h2 className="f3 fs-sm-16 fs-md-20 fs-lg-22 fs-xl-24 fs-xxl-26">
          Total: {count}
        </h2>
        <div className="d-flex gap-3">
          <div className="search-input">
            <input
              type="text"
              placeholder="Search"
              style={{ border: "none", width: "150px" }}
              onChange={(e) => handleChangeFilter("search", e.target.value)}
              value={selectedFilter.search}
            />
            <img src={Search_icon} className="search" alt="search" />
          </div>
          {checkPermission("CAN-ADD-ADMIN_LIST") && (
            <button
              onClick={() =>
                navigate("/add-admin", {
                  state: { type: "add", page: page },
                })
              }
              className="f2 fs-sm-10 fs-md-12 fs-lg-13 fs-xl-14 fs-xxl-16 white add-btn"
            >
              + Add Admin
            </button>
          )}
        </div>
      </div>

      <Table
        columns={columns}
        setPageno={setPage}
        pagecount={pagination}
        data={adminList}
        show={true}
      />

      {popup && (
        <div className="img-popup-design">
          <div className="img-popup">
            <p className="f3 ms-2 fs-xs-10 fs-sm-11 fs-md-12 fs-lg-13 fs-xl-14 fs-xxl-15 mt-4">
              Are you sure you want to Delete?
            </p>
            <div className="d-flex ac-jb px-2 mt-5">
              <button onClick={handleDelete} className="add-btn w-40">
                Yes
              </button>
              <button
                onClick={() => setPopup(false)}
                className="back-btn f2 w-40"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminList;
